import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Link } from 'core/i18n/navigation';
import { useTranslations } from 'next-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faXmark,
  faChevronDown,
} from '@fortawesome/pro-regular-svg-icons';
import { appRoutes } from 'config';
import {
  DOWNLOAD,
  MenuLink,
  PLANS,
  PRODUCT,
  RESOURCES,
  USE_CASES,
  resolveHref,
} from './NavMenu';
import Logotype from 'assets/icons/logotype.svg';

type Section = 'product' | 'plans' | 'resources' | null;

interface Props {
  /** Hamburger tint: white over a dark/transparent header, dark over white. */
  onDark: boolean;
  /** Marketing-theme-aware pages: tint follows `.marketing-dark` instead of onDark. */
  themed?: boolean;
}

const MobileItem = ({
  link,
  onNavigate,
}: {
  link: MenuLink;
  onNavigate: () => void;
}) => {
  const t = useTranslations();
  const muted = link.comingSoon;
  const inner = (
    <>
      <FontAwesomeIcon
        icon={link.icon}
        className={classNames(
          styles.item.icon,
          muted ? 'text-mf-gray-300' : 'text-mf-blue',
        )}
      />
      <span className={styles.item.text}>
        <span
          className={classNames(
            styles.item.title,
            muted ? 'text-mf-gray-400' : 'text-mf-off-black-600',
          )}
        >
          {t(`nav.items.${link.key}.title`)}
        </span>
        <span
          className={classNames(
            styles.item.desc,
            muted ? 'text-mf-gray-300' : 'text-mf-gray-400',
          )}
        >
          {link.comingSoon && (
            <span className={styles.item.badge}>{t('nav.comingSoon')}</span>
          )}
          {t(`nav.items.${link.key}.desc`)}
        </span>
      </span>
    </>
  );

  if (link.comingSoon) {
    return (
      <span className={styles.item.root} aria-disabled="true">
        {inner}
      </span>
    );
  }

  const href = resolveHref(link);

  // External links keep a plain anchor (new tab); internal links use next-intl's
  // locale-aware <Link> for consistent SSR/client hrefs + soft navigation.
  if (link.external) {
    return (
      <a
        href={href}
        className={styles.item.root}
        onClick={onNavigate}
        target="_blank"
        rel="noreferrer noopener"
      >
        {inner}
      </a>
    );
  }

  return (
    <Link href={href} className={styles.item.root} onClick={onNavigate}>
      {inner}
    </Link>
  );
};

const Accordion = ({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => (
  <div>
    <button
      type="button"
      className={styles.acc.button}
      aria-expanded={open}
      onClick={onToggle}
    >
      {label}
      <FontAwesomeIcon
        icon={faChevronDown}
        className={classNames(styles.acc.chevron, open && 'rotate-180')}
      />
    </button>
    {open && <div className={styles.acc.panel}>{children}</div>}
  </div>
);

export const MobileMenu = ({ onDark, themed = false }: Props) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<Section>('product');
  // The drawer is portalled to <body> so its `position: fixed` is relative to
  // the viewport — the header's backdrop-blur (when scrolled) would otherwise
  // make the nav a containing block and clip the drawer.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const close = () => setOpen(false);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemProps = { onNavigate: close };

  return (
    <div className="lg:hidden">
      <button
        type="button"
        className={styles.toggle}
        aria-label={t('nav.openMenu')}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <FontAwesomeIcon
          icon={faBars}
          className={classNames(
            styles.barIcon,
            themed
              ? 'text-mf-off-black-600 marketing-dark:text-white'
              : onDark
                ? 'text-white'
                : 'text-mf-off-black-600',
          )}
        />
      </button>

      {mounted &&
        createPortal(
          <div className="lg:hidden">
            {/* Backdrop */}
            <div
              className={classNames(
                styles.backdrop,
                open ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
              onClick={close}
              aria-hidden="true"
            />

            {/* Slide-in drawer */}
            <div
              className={classNames(
                styles.drawer,
                open ? 'translate-x-0' : 'translate-x-full',
              )}
              role="dialog"
              aria-modal="true"
              aria-hidden={!open}
            >
              <div className={styles.top}>
                <Link
                  href={appRoutes.HOME}
                  onClick={close}
                  className="flex items-center"
                >
                  <Logotype
                    className="h-4 w-auto text-[#0E1738]"
                    role="img"
                    aria-label="MindFeed"
                  />
                </Link>
                <button
                  type="button"
                  className={styles.close}
                  aria-label={t('nav.closeMenu')}
                  onClick={close}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="h-[18px] w-[18px]"
                  />
                </button>
              </div>

              <div className={styles.body}>
                <Accordion
                  label={t('nav.product')}
                  open={section === 'product'}
                  onToggle={() =>
                    setSection((s) => (s === 'product' ? null : 'product'))
                  }
                >
                  {PRODUCT.map((link) => (
                    <MobileItem key={link.key} link={link} {...itemProps} />
                  ))}
                  <div className={styles.subLabel}>{t('nav.useCases')}</div>
                  {USE_CASES.map((link) => (
                    <MobileItem key={link.key} link={link} {...itemProps} />
                  ))}
                  <div className={styles.subLabel}>{t('nav.download')}</div>
                  {DOWNLOAD.map((link) => (
                    <MobileItem key={link.key} link={link} {...itemProps} />
                  ))}
                </Accordion>

                <Accordion
                  label={t('nav.plans')}
                  open={section === 'plans'}
                  onToggle={() =>
                    setSection((s) => (s === 'plans' ? null : 'plans'))
                  }
                >
                  {PLANS.map((sec) => (
                    <div key={sec.labelKey}>
                      <div className={styles.subLabel}>{t(sec.labelKey)}</div>
                      {sec.items.map((link) => (
                        <MobileItem key={link.key} link={link} {...itemProps} />
                      ))}
                    </div>
                  ))}
                </Accordion>

                <Accordion
                  label={t('nav.resources')}
                  open={section === 'resources'}
                  onToggle={() =>
                    setSection((s) => (s === 'resources' ? null : 'resources'))
                  }
                >
                  {RESOURCES.map((sec) => (
                    <div key={sec.labelKey}>
                      <div className={styles.subLabel}>{t(sec.labelKey)}</div>
                      {sec.items.map((link) => (
                        <MobileItem key={link.key} link={link} {...itemProps} />
                      ))}
                    </div>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const styles = {
  toggle: 'flex h-10 w-10 items-center justify-center focus:outline-none',
  barIcon: 'h-[22px] w-[22px] transition-colors',
  backdrop: 'fixed inset-0 z-[80] bg-black/40 transition-opacity duration-300',
  // Full-width on phones; a constrained, softly rounded panel from `sm` up.
  drawer: classNames(
    'fixed inset-y-0 right-0 z-[90] flex w-full flex-col overflow-hidden bg-white',
    'sm:w-[368px] sm:rounded-l-[22px]',
    'shadow-[0_0_60px_rgba(0,0,0,0.25)] transition-transform duration-300 ease-out',
  ),
  top: 'flex h-14 flex-shrink-0 items-center justify-between border-b border-mf-gray-200 px-5',
  close:
    'flex h-9 w-9 items-center justify-center rounded-full bg-mf-gray-50 text-mf-off-black-600 transition-colors hover:bg-mf-gray-100',
  body: 'flex-1 overflow-y-auto px-5 pb-8',
  subLabel:
    'px-px pb-1 pt-[14px] text-[11px] font-bold uppercase tracking-[0.1em] text-mf-gray-400',
  acc: {
    // Lighter, smaller headers — closer to the desktop nav's refinement.
    button:
      'flex w-full items-center justify-between border-b border-mf-gray-200 py-3.5 text-[15px] font-semibold tracking-[-0.01em] text-mf-off-black-600',
    chevron: 'h-4 w-4 text-mf-gray-400 transition-transform duration-200',
    panel: 'pb-2',
  },
  item: {
    root: 'flex items-start gap-3 py-2',
    icon: 'mt-0.5 h-[19px] w-[19px] flex-shrink-0',
    text: 'flex min-w-0 flex-col gap-0.5',
    titleRow: 'flex items-center gap-2',
    title: 'text-[14px] font-semibold leading-tight',
    desc: 'text-[12px] leading-snug',
    badge:
      'mr-1.5 inline-flex translate-y-[-1px] items-center whitespace-nowrap rounded-full bg-[linear-gradient(90deg,#2757BA,#7B5CE6)] px-1.5 py-[2px] align-middle text-[8.5px] font-bold uppercase leading-none tracking-[0.06em] text-white shadow-[0_2px_6px_rgba(39,87,186,0.35)]',
  },
};
